import { useState } from 'react';
import TitleBlock from '../TitleBlock/TitleBlock';
import AreaList from '../UI/AreaList/AreaList';
import AreaListData from '../UI/AreaListData/AreaListData';
import VariableDropdown from '../../Variables/VariablesDropdown/VariablesDropdown';
import BranchRow from './BranchRow/BranchRow';
import { useTreeDispatchContext, useTreeStateContext } from '../../../contexts/TreeContext';
import binarySearchString from '../../../utils/binarySearchString';
import generateObjectId from '../../../utils/generateObjectId';
import { BlockActionCategorizationDefault } from '../../../data/constant/blocks/BlockActionCategorizationDefault';
import editorAction from '../../../actions/editorAction';

function FormActionCategorization({ id }) {
    const { children, blocks } = useTreeStateContext();
    const indexBlock = binarySearchString(blocks, id, 'object_id');
    const block = blocks[indexBlock] ? blocks[indexBlock] : BlockActionCategorizationDefault;
    const name = block.name;
    const branches = block.data.branches || [];
    const variable = block.data.variable || null;
    const variable_redirect = block.data.variable_redirect || null;

    const childrenBlocks = children.filter((child) => child.parent_block_id == id).sort((a, b) => a.index - b.index);
    const realChildren = childrenBlocks.filter((child) => child.index != 0);
    const phantomChild = childrenBlocks.find((child) => child.index == 0);

    const dispatch = useTreeDispatchContext();
    const { updateBlockData, createBlockChild, updateBlockChild, deleteBlockChild, updateBlockChildren, checkErrorsForPath } = editorAction();
    const [isAddingBranch, setIsAddingBranch] = useState(false);

    const rows = realChildren.map((child, idx) => {
        const branch = branches.find((b) => b.branch_id === child.object_id);
        const is_default = branch ? !!branch.is_default : (branches.length === 0 && idx === realChildren.length - 1);
        return {
            branch_id: child.object_id,
            values: branch ? branch.values : [],
            forbidden: branch ? branch.forbidden || [] : [],
            is_default,
            canDelete: realChildren.length > 1 && !is_default,
            child,
            index: idx,
        };
    });

    const saveBranches = (newRows) => {
        const newBranches = newRows.map(({ branch_id, values, forbidden, is_default }) => ({ branch_id, values, forbidden: forbidden || [], is_default }));
        dispatch({ type: 'updateBlockData', payload: { id, data: { branches: newBranches } } });
        updateBlockData(id, { branches: newBranches }, { branches: newBranches });
    };

    const setRowValue = (branch_id, field, value) => {
        if (field === 'text') {
            const child = realChildren.find((c) => c.object_id === branch_id);
            if (!child || child.text === value) return;
            dispatch({ type: 'updateBlockChild', payload: { block_id: id, object_id: branch_id, data: { text: value } } });
            updateBlockChild(id, branch_id, { text: value });
            return;
        }

        const newRows = rows.map((row) => (row.branch_id === branch_id ? { ...row, [field]: value } : row));
        saveBranches(newRows);
    };

    const setDefaultRow = (branch_id) => {
        const newRows = rows.map((row) => ({ ...row, is_default: row.branch_id === branch_id }));
        saveBranches(newRows);
    };

    const setVariable = (_name, variableObj) => {
        const data = { variable: variableObj || null };
        dispatch({ type: 'updateBlockData', payload: { id, data } });
        updateBlockData(id, data, { keyword_var: variableObj ? variableObj.name : null });
    };

    const setVariableRedirect = (_name, variableObj) => {
        const data = { variable_redirect: variableObj || null };
        dispatch({ type: 'updateBlockData', payload: { id, data } });
        updateBlockData(id, data, { keyword_redirect_var: variableObj ? variableObj.name : null });
    };

    const addBranch = async (scroll) => {
        if (isAddingBranch) return;
        setIsAddingBranch(true);

        const length = realChildren.length + 1;
        const lastChild = childrenBlocks[childrenBlocks.length - 1];

        const child = {
            text: 'Branch ' + length,
            object_id: generateObjectId(length),
            index: length,
            parent_block_id: id,
        };

        dispatch({ type: 'createBlockChildBeforeLast', payload: { block_id: id, data: child } });
        await createBlockChild(id, child);

        if (lastChild) {
            updateBlockChild(id, lastChild.object_id, { index: length + 1 });
        }

        saveBranches([...rows, { branch_id: child.object_id, values: [], forbidden: [], is_default: false }]);
        checkErrorsForPath();

        if (scroll) scroll();
        setIsAddingBranch(false);
    };

    const deleteBranch = ({ branch_id }) => {
        if (rows.length <= 1) return;
        if (rows.find((row) => row.branch_id === branch_id)?.is_default) return;

        dispatch({ type: 'deleteBlockChildAndOrderOther', payload: { block_id: id, object_id: branch_id } });
        deleteBlockChild(id, branch_id);

        const remainingReal = realChildren.filter((c) => c.object_id !== branch_id).map((c, idx) => ({ ...c, index: idx + 1 }));
        const fullList = phantomChild ? [phantomChild, ...remainingReal] : remainingReal;
        dispatch({ type: 'updateBlockChildren', payload: { block_id: id, children: fullList } });
        updateBlockChildren(id, fullList);

        saveBranches(rows.filter((row) => row.branch_id !== branch_id));
    };

    const moveBranch = (indexStart, indexDestination) => {
        if (indexDestination < 0 || indexDestination >= realChildren.length) return;

        const newReal = [...realChildren];
        const [moved] = newReal.splice(indexStart, 1);
        newReal.splice(indexDestination, 0, moved);
        const reindexedReal = newReal.map((c, idx) => ({ ...c, index: idx + 1 }));
        const fullList = phantomChild ? [phantomChild, ...reindexedReal] : reindexedReal;

        dispatch({ type: 'updateBlockChildren', payload: { block_id: id, children: fullList } });
        updateBlockChildren(id, fullList);
    };

    const functionsRow = { setValue: setRowValue, setDefault: setDefaultRow, deleteValue: deleteBranch, move: moveBranch };

    return (
        <>
            <TitleBlock title="Categorization" titleInput="Block name" name={name} id={id} />
            <div className="area-edit">
                <div className="grid-row">
                    <label className="dropdown_title"> Categorize : </label>
                    <div className="dropdown_container">
                        <VariableDropdown variable={variable} name="variable" setValue={setVariable} />
                    </div>
                </div>
                <div className="grid-row">
                    <label className="dropdown_title"> Save result in : </label>
                    <div className="dropdown_container">
                        <VariableDropdown variable={variable_redirect} name="variable_redirect" setValue={setVariableRedirect} />
                    </div>
                </div>
                <AreaList title="Branches" maxHeight="430px" buttonLabel="Add a Branch" onClickButton={(arg) => addBranch(...arg)} buttonDisabled={isAddingBranch}>
                    <AreaListData data={rows} ComponentRow={BranchRow} FunctionsRow={functionsRow} componentKey="branch_id" />
                </AreaList>
            </div>
        </>
    );
}

export default FormActionCategorization;
